import { farShared } from "./far/farTokens.jsx";

/**
 * Share Link modal layout (My Docs Share Link spec §11): ~750 px dialog, stacked sections, a body
 * that scrolls while the header and footer stay reachable, FAR state tones. Classes prefixed "slm".
 * Merged with shareLinkModalStyle (inputs, editor) and sendPageStyle (cards, chips, presets) so the
 * modal uses the Send page's design tokens in the light and dark themes.
 */
const shareLinkLayoutStyle = (theme) => {
    const s = farShared(theme);
    const t = s.t;
    const button = {
        ...s.focusRing,
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px",
        minHeight: "46px", padding: "0 24px", borderRadius: "10px", fontSize: "16px", fontWeight: 700, cursor: "pointer",
        "&:disabled": { backgroundColor: t.disabledBg, color: t.disabledText, borderColor: t.disabledBg, cursor: "not-allowed" }
    };
    const tone = (c) => ({ color: c.fg, backgroundColor: c.bg, borderColor: c.border, "& > svg": { color: c.solid } });
    return {
        slmPaper: {
            // vh, not %: the global app CSS lets .MuiDialog-container grow with its content.
            width: "100%", maxWidth: "760px", margin: "16px", maxHeight: "calc(100vh - 32px)", boxSizing: "border-box",
            display: "flex", flexDirection: "column", overflow: "hidden",
            backgroundColor: t.dark ? "#101627" : "#F6F7FB", backgroundImage: "none", color: t.text,
            borderRadius: "16px", border: "1px solid "+t.border, textAlign: "left",
            [theme.breakpoints.down("xs")]: { margin: "8px", maxHeight: "calc(100vh - 16px)", borderRadius: "12px" }
        },
        slmHeader: {
            display: "flex", alignItems: "flex-start", gap: "14px", padding: "22px 24px 16px", flexShrink: 0,
            borderBottom: "1px solid "+t.border,
            [theme.breakpoints.down("xs")]: { padding: "16px 14px 12px" }
        },
        slmHeaderIcon: { color: t.link, width: "32px !important", height: "32px !important", transform: "rotate(-45deg)", flexShrink: 0, marginTop: "2px" },
        slmHeaderText: { flex: 1, minWidth: 0 },
        slmTitle: { margin: 0, fontSize: "26px", lineHeight: "32px", fontWeight: 700, color: t.textStrong },
        slmSubtitle: { margin: "4px 0 0", fontSize: "15px", color: t.textMuted },
        slmClose: {
            ...s.focusRing, background: "none", border: "none", color: t.textMuted, cursor: "pointer", padding: "6px", borderRadius: "8px",
            display: "inline-flex", "&:hover": { backgroundColor: t.surfaceHover, color: t.textStrong }, "&:disabled": { opacity: 0.4, cursor: "default" },
            "& svg": { width: "24px", height: "24px" }
        },
        slmBody: {
            flex: "1 1 auto", overflowY: "auto", padding: "18px 24px", display: "flex", flexDirection: "column", gap: "16px",
            // Sections keep their natural height; the body scrolls instead of squeezing them.
            "& > *": { flexShrink: 0 },
            "& > section": { margin: 0 },
            [theme.breakpoints.down("xs")]: { padding: "14px" }
        },
        // Recipient chips: replaces the older tagsInput rules (black text on the dark modal).
        tagsInput: {
            display: "flex", alignItems: "flex-start", gap: "8px", width: "100%", boxSizing: "border-box", textAlign: "left",
            margin: "0 0 8px", padding: "6px 8px", borderRadius: "10px",
            backgroundColor: t.inputBg, border: "1px solid "+t.border,
            "& .users-icon": {
                display: "flex", alignItems: "center", minHeight: "30px", flexShrink: 0,
                "& svg": { width: "20px", height: "20px", color: t.textMuted, fill: t.textMuted }
            },
            "& .users-lists": { display: "flex", flexWrap: "wrap", gap: "6px", padding: 0, flex: 1, minWidth: 0 },
            "& .react-tagsinput-tag": {
                display: "inline-flex", alignItems: "center", gap: "4px", maxWidth: "100%", margin: 0,
                padding: "4px 8px 4px 10px", borderRadius: "999px", border: "1px solid "+t.border,
                backgroundColor: t.surfaceAlt+" !important", color: t.textStrong+" !important",
                fontSize: "14px", fontWeight: 500, lineHeight: "20px", textTransform: "none",
                "& > span": {
                    color: t.textStrong+" !important", backgroundColor: "transparent !important", border: "none",
                    padding: 0, borderRadius: 0, overflowWrap: "anywhere", cursor: "pointer"
                },
                "& a": {
                    ...s.focusRing, position: "static", color: t.textMuted+" !important", textDecoration: "none", borderRadius: "999px",
                    "&::before": { color: t.textMuted+" !important" },
                    "&:hover::before, &:focus::before": { color: t.danger.fg+" !important" }
                }
            }
        },
        // Long placeholder stays on one line (ellipsis) on narrow screens.
        slmToField: { "& label": { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "calc(100% - 24px)" } },
        slmHelp: { margin: "8px 0 0 !important", fontSize: "13px", color: t.textMuted },
        slmFieldError: { margin: "6px 0 0", fontSize: "13px", color: t.danger.fg, fontWeight: 600 },
        slmFileSingle: { display: "flex", gap: "16px", alignItems: "center" },
        slmThumb: { width: "104px", height: "104px", objectFit: "cover", flexShrink: 0, borderRadius: "10px", border: "2px solid "+(t.dark ? "#FFFFFF" : t.border), display: "block" },
        slmThumbIcon: {
            width: "64px", height: "64px", flexShrink: 0, borderRadius: "10px", border: "1px solid "+t.border, backgroundColor: t.surfaceAlt, color: t.textMuted,
            display: "inline-flex", alignItems: "center", justifyContent: "center", "& svg": { width: "32px", height: "32px" }
        },
        slmThumbSmall: {
            width: "38px", height: "38px", flexShrink: 0, borderRadius: "8px", border: "1px solid "+t.border, backgroundColor: t.surfaceAlt, color: t.textMuted,
            display: "inline-flex", alignItems: "center", justifyContent: "center", "& svg": { width: "20px", height: "20px" }
        },
        slmFileText: { display: "flex", flexDirection: "column", gap: "3px", minWidth: 0, flex: 1 },
        slmFileName: { margin: 0, fontSize: "16px", fontWeight: 700, color: t.textStrong, overflowWrap: "anywhere" },
        slmFileMeta: { margin: 0, fontSize: "14px", color: t.textMuted, overflowWrap: "anywhere" },
        slmFileList: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "10px" },
        slmFileRow: { display: "flex", alignItems: "center", gap: "12px" },
        slmShield: { color: t.success.solid+" !important" },
        slmIntro: { margin: 0, fontSize: "14px", lineHeight: "20px", color: t.textMuted },
        slmState: {
            display: "flex", alignItems: "flex-start", gap: "10px", padding: "12px 14px", borderRadius: "10px", border: "1px solid "+t.border,
            fontSize: "14px", lineHeight: "20px",
            "& > svg": { width: "22px", height: "22px", flexShrink: 0, marginTop: "1px" },
            "& > div": { display: "flex", flexDirection: "column", gap: "2px", minWidth: 0, flex: 1 },
            "& strong": { fontWeight: 700 },
            "& .sd_loader": { width: "22px !important", height: "22px !important", flexShrink: 0 }
        },
        slmState_neutral: { color: t.text, backgroundColor: t.surfaceAlt, borderColor: t.border, "& > svg": { color: t.textMuted } },
        slmState_success: tone(t.success),
        slmState_warning: tone(t.warning),
        slmState_danger: tone(t.danger),
        slmStateActions: { display: "flex", flexWrap: "wrap", gap: "8px" },
        slmTextButton: { ...s.focusRing, background: "none", border: "none", padding: 0, color: t.link, fontWeight: 600, fontSize: "14px", cursor: "pointer", textDecoration: "underline", alignSelf: "center" },
        slmIssues: {
            listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "6px", fontSize: "14px", color: t.text,
            "& li": { display: "flex", gap: "8px", alignItems: "flex-start", overflowWrap: "anywhere" },
            "& svg": { color: t.danger.solid, width: "18px", height: "18px", flexShrink: 0, marginTop: "1px" }
        },
        slmDivider: { border: 0, borderTop: "1px solid "+t.border, margin: "2px 0", width: "100%" },
        slmChecksTitle: { margin: 0, fontSize: "15px", fontWeight: 700, color: t.textStrong },
        slmChecks: {
            listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px 16px",
            "& li": { display: "flex", alignItems: "center", gap: "10px", fontSize: "14px", color: t.text },
            "& svg": { color: t.accent, width: "22px", height: "22px", flexShrink: 0 },
            [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" }
        },
        slmRulesPaper: {
            width: "100%", maxWidth: "620px", margin: "16px", maxHeight: "calc(100vh - 32px)", display: "flex", flexDirection: "column",
            backgroundColor: t.dark ? "#121829" : "#FFFFFF", backgroundImage: "none", color: t.text, borderRadius: "14px", border: "1px solid "+t.border, textAlign: "left"
        },
        slmRulesHeader: { display: "flex", alignItems: "center", gap: "12px", padding: "16px 20px", borderBottom: "1px solid "+t.border },
        slmRulesTitle: { margin: 0, flex: 1, fontSize: "18px", fontWeight: 700, color: t.textStrong },
        slmRulesBody: { padding: "16px 20px 20px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "12px" },
        slmSent: {
            display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "8px", padding: "32px 8px",
            "& > svg": { color: t.success.solid, width: "56px", height: "56px" },
            "& h3": { margin: 0, fontSize: "22px", fontWeight: 700, color: t.textStrong },
            "& p": { margin: 0, fontSize: "15px", color: t.text, maxWidth: "520px" }
        },
        slmFooter: {
            display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: "10px 16px", flexShrink: 0,
            borderTop: "1px solid "+t.border, padding: "14px 24px 18px",
            [theme.breakpoints.down("xs")]: { padding: "12px 14px 14px" }
        },
        slmStatus: { flex: "1 1 240px", margin: 0, fontSize: "14px", color: t.textMuted, minHeight: "20px" },
        slmButtons: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px", justifyContent: "flex-end" },
        slmSend: {
            ...button, border: "1px solid "+t.accent, backgroundColor: t.accent, color: t.accentText, minWidth: "170px",
            "& svg": { fill: "currentColor" }, "& .sd_loader": { width: "22px !important", height: "22px !important" },
            "&:hover:not(:disabled)": { filter: "brightness(1.08)" }
        },
        slmCancel: {
            ...button, border: "2px solid "+t.link, backgroundColor: "transparent", color: t.link, minWidth: "120px",
            "&:hover:not(:disabled)": { backgroundColor: t.surfaceHover }
        },
        slmDiscard: { ...button, border: "1px solid "+t.danger.solid, backgroundColor: t.danger.solid, color: t.danger.onSolid, minWidth: "120px" },
        slmConfirm: {
            display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px 16px", width: "100%",
            "& > span": { flex: "1 1 240px", fontSize: "14px", fontWeight: 600, color: t.textStrong }
        },
        [theme.breakpoints.down("xs")]: {
            slmButtons: { width: "100%" },
            slmSend: { flex: "1 1 auto" },
            slmCancel: { flex: "1 1 auto" },
            slmDiscard: { flex: "1 1 auto" }
        }
    };
};

export default shareLinkLayoutStyle;
